/**
 * ConversationCommandPanel — command-mode overlay rendered inside the composer.
 * Shows search results, selection state, chips strip, and mode banners.
 *
 * Used by ConversationComposerPanel when a slash command is active.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, X, AlertCircle, RefreshCw } from 'lucide-react';
import { lookupFinanceCommandOptionsQueryKey } from '../../generated/api/@tanstack/react-query.gen.js';
import { financeCommandsApi } from '../../services/api/finance-commands.api.js';
import { Button } from '../ui/Button.js';
import { Skeleton } from '../ui/Skeleton.js';
import type { CommandName, CommandSelection, SelectionKey } from '../../hooks/useConversationCommands.js';
import type { FinanceCommandLookupOption } from '../../generated/api/types.gen.js';

// ─── Provenance chip ──────────────────────────────────────────────────────────

function sourceLabel(opt: FinanceCommandLookupOption): string {
  if (opt.installed) return 'installed';
  const src = opt.source.toLowerCase();
  if (src.includes('bank')) return 'bank';
  if (src.includes('manual')) return 'manual';
  if (src.includes('preset')) return 'preset';
  return opt.source;
}

// ─── Selection chip strip ─────────────────────────────────────────────────────

interface ChipStripProps {
  selections: Map<SelectionKey, CommandSelection>;
  researchObjective: string | null;
  onRemoveSelection: (key: SelectionKey) => void;
  onRemoveResearch: () => void;
  onClearAll: () => void;
}

export const CommandChipStrip: React.FC<ChipStripProps> = ({
  selections,
  researchObjective,
  onRemoveSelection,
  onRemoveResearch,
  onClearAll,
}) => {
  const chips = Array.from(selections.entries());
  const totalCount = chips.length + (researchObjective ? 1 : 0);
  if (totalCount === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map(([key, sel]) => {
        const prefix =
          sel.type === 'indicator'
            ? 'ind'
            : sel.type === 'strategy'
              ? 'strat'
              : sel.type === 'timeframe'
                ? 'tf'
                : sel.type;
        return (
          <span
            key={key}
            className="inline-flex items-center gap-1 rounded-lg border border-card-border bg-background-gray-secondary_alt py-0.5 pr-0.5 pl-2 text-[11px] text-text-secondary"
          >
            <span className="font-mono text-text-tertiary">{prefix}:</span>
            <span className="max-w-[100px] truncate">{sel.label}</span>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              className="size-5 hover:text-danger"
              onClick={() => onRemoveSelection(key)}
              aria-label={`Remove ${sel.label}`}
            >
              <X size={10} aria-hidden />
            </Button>
          </span>
        );
      })}

      {researchObjective && (
        <span className="inline-flex items-center gap-1 rounded-lg border border-card-border bg-background-gray-secondary_alt py-0.5 pr-0.5 pl-2 text-[11px] text-text-secondary">
          <span className="font-mono text-text-tertiary">research:</span>
          <span className="max-w-[140px] truncate">
            {researchObjective.length > 40 ? `${researchObjective.slice(0, 40)}…` : researchObjective}
          </span>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            className="size-5 hover:text-danger"
            onClick={onRemoveResearch}
            aria-label="Remove research objective"
          >
            <X size={10} aria-hidden />
          </Button>
        </span>
      )}

      {totalCount > 2 && (
        <Button variant="ghost" size="sm" className="h-auto p-0 text-[11px] underline" onClick={onClearAll}>
          clear all
        </Button>
      )}
    </div>
  );
};

// ─── Results panel (lookup mode) ──────────────────────────────────────────────

interface ResultsPanelProps {
  command: CommandName;
  searchQuery: string;
  selections: Map<SelectionKey, CommandSelection>;
  highlightIndex: number;
  onToggle: (opt: FinanceCommandLookupOption) => void;
  onOptionsChange?: (options: FinanceCommandLookupOption[]) => void;
}

export const CommandResultsPanel: React.FC<ResultsPanelProps> = ({
  command,
  searchQuery,
  selections,
  highlightIndex,
  onToggle,
  onOptionsChange,
}) => {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: lookupFinanceCommandOptionsQueryKey({
      query: { command, ...(searchQuery ? { q: searchQuery } : {}) },
    }),
    queryFn: () => financeCommandsApi.lookup(command, searchQuery || undefined),
    enabled: true,
    staleTime: 30_000,
  });

  const options = React.useMemo(() => data?.options ?? [], [data?.options]);

  React.useEffect(() => {
    onOptionsChange?.(options);
  }, [onOptionsChange, options]);

  if (isLoading) {
    return (
      <div className="space-y-1.5 px-2.5 py-1.5" data-testid="command-panel-loading">
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} height={32} rounded="rounded-md" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 px-2.5 py-2 text-xs text-danger" data-testid="command-panel-error">
        <AlertCircle size={13} aria-hidden />
        <span>Failed to load options.</span>
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => refetch()}>
          <RefreshCw size={11} aria-hidden /> retry
        </Button>
      </div>
    );
  }

  if (options.length === 0) {
    return (
      <div className="px-2.5 py-2 text-xs text-text-tertiary" data-testid="command-panel-empty">
        No matches — keep typing
      </div>
    );
  }

  return (
    <div className="max-h-[220px] overflow-y-auto" data-testid="command-panel-results">
      {options.map((opt, idx) => {
        const key: SelectionKey = `${opt.type}:${opt.id}`;
        const isSelected = selections.has(key);
        const isHighlighted = idx === highlightIndex;
        return (
          <Button
            key={key}
            variant="ghost"
            onClick={() => onToggle(opt)}
            data-highlighted={isHighlighted || undefined}
            aria-selected={isHighlighted}
            className={`h-auto w-full justify-start gap-2 rounded-none px-2.5 py-2 text-left font-normal ${
              isHighlighted ? 'bg-background-gray-secondary' : ''
            }`}
          >
            <span className={`w-3.5 shrink-0 ${isSelected ? 'text-primary-500' : 'text-transparent'}`}>
              <Check size={12} aria-hidden />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-xs text-title-50">{opt.label}</span>
              {opt.description && (
                <span className="block truncate text-[11px] text-text-tertiary">{opt.description}</span>
              )}
            </span>
            <span className="shrink-0 font-mono text-[10px] text-text-tertiary">{sourceLabel(opt)}</span>
          </Button>
        );
      })}
      {data?.truncated && (
        <div className="border-t border-card-border px-2.5 py-1.5 text-[11px] text-text-tertiary">
          Refine your search to see more results
        </div>
      )}
    </div>
  );
};

// ─── Main panel export ────────────────────────────────────────────────────────

interface ConversationCommandPanelProps {
  activeCommand: CommandName;
  searchQuery: string;
  selections: Map<SelectionKey, CommandSelection>;
  highlightIndex: number;
  onToggle: (opt: FinanceCommandLookupOption) => void;
  onOptionsChange?: (options: FinanceCommandLookupOption[]) => void;
}

export const ConversationCommandPanel: React.FC<ConversationCommandPanelProps> = ({
  activeCommand,
  searchQuery,
  selections,
  highlightIndex,
  onToggle,
  onOptionsChange,
}) => {
  const isPromptMode = activeCommand === 'research';

  const bannerText = isPromptMode
    ? 'Research objective — Enter or Apply to attach'
    : `Searching ${activeCommand}s — ↑↓ navigate, Enter to select · Apply or Esc to finish`;

  return (
    <div className="overflow-hidden rounded-lg border border-card-border bg-surface" data-testid="command-panel">
      <div className="border-b border-card-border bg-background-gray-secondary_alt px-2.5 py-1.5 font-mono text-[11px] text-text-tertiary">
        {bannerText}
      </div>

      <CommandResultsPanel
        command={activeCommand}
        searchQuery={isPromptMode ? '' : searchQuery}
        selections={selections}
        highlightIndex={highlightIndex}
        onToggle={onToggle}
        onOptionsChange={onOptionsChange}
      />
    </div>
  );
};
